import { useCallback, useEffect, useRef, useState } from 'react';
import { getDeviceInfoApiUrl } from '../config';

type SpeakOptions = {
  lang?: string;
  voiceName?: string;
  rate?: number;
  pitch?: number;
  volume?: number;
};

export const useSpeech = () => {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isVoiceLoading, setIsVoiceLoading] = useState(false);
  const [voices] = useState<SpeechSynthesisVoice[]>([]);
  const [preferredVoiceName, setPreferredVoiceName] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    cleanupAudio();
    if (mountedRef.current) {
      setIsVoiceLoading(false);
      setIsSpeaking(false);
    }
  }, [cleanupAudio]);

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) audioRef.current.pause();
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.play().catch(() => {});
    }
  }, []);

  const setVoiceByName = useCallback((voiceName: string | null) => {
    setPreferredVoiceName(voiceName);
  }, []);

  const speakWithSynthesis = useCallback((text: string, opts?: SpeakOptions) => {
    if (!synth || !text) return false;
    try {
      if (synth.speaking || synth.paused) synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = opts?.lang || 'en-US';
      u.rate = opts?.rate ?? 1;
      u.pitch = opts?.pitch ?? 1;
      u.volume = opts?.volume ?? 1;
      u.onstart = () => mountedRef.current && setIsSpeaking(true);
      u.onend = () => mountedRef.current && setIsSpeaking(false);
      u.onerror = () => mountedRef.current && setIsSpeaking(false);
      synth.speak(u);
      return true;
    } catch {
      return false;
    }
  }, [synth]);

  const speak = useCallback(
    async (text: string, opts?: SpeakOptions) => {
      const deviceInfoApiUrl = getDeviceInfoApiUrl();
      if (!text || !deviceInfoApiUrl || isVoiceLoading) return;

      try {
        stop();
        if (mountedRef.current) setIsVoiceLoading(true);

        if (typeof MediaSource === 'undefined') {
          const fallbackStarted = speakWithSynthesis(text, opts);
          if (!fallbackStarted) throw new Error('Speech fallback unavailable');
          return;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const selectedVoice = opts?.voiceName || preferredVoiceName || 'nova';
        const response = await fetch(
          `${deviceInfoApiUrl}/tts/stream?text=${encodeURIComponent(text)}&voice=${encodeURIComponent(selectedVoice)}`,
          { signal: controller.signal }
        );

        if (!response.body) {
          throw new Error('No stream body in response');
        }

        const mimeCodec = 'audio/mpeg';
        if (!MediaSource.isTypeSupported(mimeCodec)) {
          const fallbackStarted = speakWithSynthesis(text, opts);
          if (!fallbackStarted) throw new Error('Codec not supported');
          return;
        }

        const mediaSource = new MediaSource();
        const audio = new Audio();
        const objectUrl = URL.createObjectURL(mediaSource);

        objectUrlRef.current = objectUrl;
        audio.src = objectUrl;
        audioRef.current = audio;

        mediaSource.addEventListener('sourceopen', async () => {
          const sourceBuffer = mediaSource.addSourceBuffer(mimeCodec);
          const reader = response.body!.getReader();

          const readChunk = async () => {
            try {
              const { done, value } = await reader.read();
              if (done) {
                if (mediaSource.readyState === 'open') mediaSource.endOfStream();
                return;
              }
              if (value) sourceBuffer.appendBuffer(value);
            } catch {
              // ignore stream errors after abort/stop
            }
          };

          sourceBuffer.addEventListener('updateend', readChunk);
          await readChunk();
        });

        await audio.play();
        if (mountedRef.current) setIsSpeaking(true);

        audio.addEventListener('ended', () => {
          if (mountedRef.current) setIsSpeaking(false);
        });
      } catch {
        const fallbackStarted = speakWithSynthesis(text, opts);
        if (!fallbackStarted && mountedRef.current) setIsSpeaking(false);
      } finally {
        if (mountedRef.current) setIsVoiceLoading(false);
      }
    },
    [isVoiceLoading, preferredVoiceName, speakWithSynthesis, stop]
  );

  const refreshVoices = useCallback(() => {}, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stop();
    };
  }, [stop]);

  return {
    speak,
    stop,
    pause,
    resume,
    setVoiceByName,
    refreshVoices,
    isSpeaking,
    isVoiceLoading,
    voices,
    preferredVoiceName,
  };
};
